import { useMemo, useState } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, RefreshCw } from "lucide-react";

import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth/useCan";

import { useRefreshRoster, useRequests, useRoster, type RosterParent } from "../api";
import { AIReviewPanel } from "../components/AIReviewPanel";
import { ChangelogPanel } from "../components/ChangelogPanel";
import { CompanyDrawer } from "../components/CompanyDrawer";
import { RelationshipDialog } from "../components/RelationshipDialog";
import { RequestsPanel } from "../components/RequestsPanel";

export function ParentCompaniesPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/parent-companies" });
  const tab = search.tab ?? "companies";
  const canWrite = useCan("parent_companies.write");

  const roster = useRoster();
  const refresh = useRefreshRoster();
  const pending = useRequests("pending");
  const [filter, setFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const parents = useMemo<RosterParent[]>(
    () => roster.data?.parents ?? [],
    [roster.data],
  );

  const filtered = useMemo(() => {
    const term = filter.toLowerCase().trim();
    if (!term) return parents;
    return parents.filter((p) => {
      const hay = [
        p.name,
        p.account_name ?? "",
        p.brand_name ?? "",
        p.city ?? "",
        p.state ?? "",
        ...p.children.map((c) => c.name),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [parents, filter]);

  const pendingTotal = pending.data?.pending_total ?? 0;
  const flaggedCount = parents.filter((p) => p.ai_status === "flagged").length;

  const setTab = (next: string) => {
    navigate({
      to: "/parent-companies",
      search: {
        ...search,
        tab:
          next === "companies"
            ? undefined
            : (next as "approvals" | "changelog" | "ai-review"),
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Parent Companies</h1>
          <p className="text-sm text-muted-foreground">
            Every Cinch company with children linked under it. Relationship
            changes proposed here need a second user's approval before they're
            pushed to Cinch, and every change is logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            title="Re-pull companies and relationships from Cinch"
          >
            <RefreshCw className={refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            Refresh from Cinch
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Propose relationship
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="Parent companies"
          value={parents.length}
          sub="companies with at least one child"
        />
        <KpiTile
          label="Linked companies"
          value={roster.data?.link_count ?? "—"}
          sub="parent → child links in Cinch"
        />
        <KpiTile
          label="Pending approvals"
          value={pendingTotal}
          sub="proposed changes awaiting a second user"
        />
        <KpiTile
          label="AI flags"
          value={flaggedCount}
          sub="parent groups the local model flagged"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="companies">Companies</TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals{pendingTotal > 0 ? ` (${pendingTotal})` : ""}
          </TabsTrigger>
          <TabsTrigger value="ai-review">
            AI review{flaggedCount > 0 ? ` (${flaggedCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="changelog">Change log</TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search parent, child, account, brand, location…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-xs"
            />
            <span className="ml-auto text-xs text-muted-foreground">
              {filtered.length} parent companies
              {roster.data?.refreshed_at
                ? ` · Cinch data as of ${new Date(roster.data.refreshed_at).toLocaleString()}`
                : ""}
            </span>
          </div>

          {roster.isLoading && (
            <p className="text-sm text-muted-foreground">Loading relationships from Cinch…</p>
          )}
          {roster.error && (
            <p className="text-sm text-destructive">{String(roster.error)}</p>
          )}

          {roster.data && (
            <Card>
              <CardContent className="p-0">
                <Table containerClassName="max-h-[calc(100vh-26rem)]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parent company</TableHead>
                      <TableHead align="right">Children</TableHead>
                      <TableHead align="right">Descendants</TableHead>
                      <TableHead>Depth</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>AI review</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody zebra>
                    {filtered.length === 0 && (
                      <TableEmpty colSpan={8}>
                        No parent companies match.
                      </TableEmpty>
                    )}
                    {filtered.map((p) => (
                      <TableRow
                        key={p.id}
                        clickable
                        onClick={() =>
                          navigate({ to: "/parent-companies", search: { company: p.id } })
                        }
                      >
                        <TableCell>
                          {/* Row click → quick view; explicit name click →
                              expanded view. stopPropagation so the bubble
                              doesn't trigger the row handler. */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate({
                                to: "/parent-companies",
                                search: { company: p.id, expanded: true },
                              });
                            }}
                            className="text-left font-medium text-brand hover:underline focus-visible:underline focus-visible:outline-none"
                            title="Open full relationship view"
                          >
                            {p.name}
                          </button>
                          <div className="text-xs text-muted-foreground">
                            {p.brand_name ?? "—"}
                            {p.parent_names.length > 0 && (
                              <> · child of {p.parent_names.join(", ")}</>
                            )}
                          </div>
                        </TableCell>
                        <TableCell numeric>{p.child_count}</TableCell>
                        <TableCell numeric>{p.descendant_count}</TableCell>
                        <TableCell>
                          {p.stacked ? (
                            <StatusBadge value="stacked" />
                          ) : (
                            <span className="text-xs text-muted-foreground">Single level</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.account_name ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                        </TableCell>
                        <TableCell>
                          {p.pending_count > 0 ? (
                            <StatusBadge value="pending" label={`${p.pending_count} pending`} />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.ai_status ? (
                            <StatusBadge value={p.ai_status} />
                          ) : (
                            <span className="text-xs text-muted-foreground">Not reviewed</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approvals" className="mt-3">
          <RequestsPanel />
        </TabsContent>

        <TabsContent value="ai-review" className="mt-3">
          <AIReviewPanel />
        </TabsContent>

        <TabsContent value="changelog" className="mt-3">
          <ChangelogPanel />
        </TabsContent>
      </Tabs>

      <CompanyDrawer />
      <RelationshipDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="add"
      />
    </div>
  );
}
